// BRK-S142 — the two halves of a CURRENCY INPUT, as pure functions.
//
// D-10 rule 3 is explicit: "Currency is formatted for display with a thousands
// separator. The wire and storage stay a plain number, never a formatted
// string." The read half of that has been honoured since BRK-S106
// (`formatBondCurrency`), and the WRITE half had not been: a currency field
// rendered as `<input type="number">`, which cannot show a separator at all —
// so a broker typing a seven-figure contract price read `2500000` and had to
// count the zeros.
//
// It is not `formatBondCurrency` with a different caller. That one formats a
// RECORD — `$250,000`, whole dollars, currency symbol included — and neither
// half of that fits an input: the symbol belongs to the control's adornment
// rather than to the editable text, and cents a broker typed must survive being
// re-read. So this pair is the input's own convention, and the two are
// deliberately separate rather than one function with a flag.
//
// THE PAIR MUST ROUND-TRIP. `parseMoneyInput(formatMoneyInput(n)) === n` for
// every finite n, which is what makes it safe for the control to reformat a
// value it is holding. A formatter that dropped a decimal, or a parser that
// choked on its own separators, would silently change a figure between two
// renders of the same number — pinned in `test/lib/currency.test.ts`.

/**
 * A number as the text a currency input SHOWS — grouped in thousands, with up
 * to two decimal places and no currency symbol.
 *
 * No symbol because the control renders `$` as an adornment: baking it into the
 * editable text means the broker can select and delete it, and every parse then
 * has to strip a character the field itself put there.
 *
 * Cents are kept when the figure has any and never invented when it does not —
 * `1234` is `1,234`, not `1,234.00`. A bond amount is typed in whole dollars
 * far more often than not, and a field that adds `.00` the moment it is touched
 * reads as though it changed the value.
 */
export function formatMoneyInput(value: number): string {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/**
 * The number a broker meant by what they typed, or **undefined** for "no
 * answer".
 *
 * Undefined rather than 0 for an empty field, because 0 IS an answer: a bond
 * with no liquidated damages and a bond whose damages nobody has entered are
 * different facts, and `isAnswered` already treats 0 as answered. Returning 0
 * here would make every untouched currency field claim to hold one.
 *
 * Grouping separators and a leading `$` are tolerated on the way in — a broker
 * pasting `$1,250,000` from a bid sheet is entering a number, not a mistake.
 * Anything still unparseable after that reads as no answer, which is what keeps
 * a half-typed `-` or `.` from reaching the wire as `NaN` (NN #7).
 */
export function parseMoneyInput(text: string): number | undefined {
  const cleaned = text.replace(/[$,\s]/g, "");
  if (cleaned === "") return undefined;
  // A bare sign or point is mid-typing, not a value. `Number("-")` is NaN and
  // `Number(".")` is NaN, but `Number("")` is 0 — so the empty check above is
  // what this relies on rather than repeating.
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}
